import * as api from "../apis/interestsApi";
import {
  useQuery,
  useQueryClient,
  queryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";


// Query options

export const searchInterestsQueryOptions = (
  adPlatformId,
  adAccountId,
  languageId,
  searchTerm
) =>
  queryOptions({
    queryKey: [
      "interests.search",
      adPlatformId,
      adAccountId,
      languageId,
      searchTerm,
    ],
    queryFn: () =>
      api.searchInterests({
        adPlatformId,
        adAccountId,
        languageId,
        searchTerm,
      }),
    // Search results are ephemeral; don’t refetch on focus and don’t keep them “fresh”
    refetchOnWindowFocus: false,
    staleTime: 0,
    // keep cache short so new searches don’t reuse old results too long
    cacheTime: 5 * 60 * 1000, // 5 minutes (default is fine too)
  });

export const getInterestsQueryOptions = () => {
  return queryOptions({
    queryKey: ["interests"],
    queryFn: api.getInterests,
    refetchOnWindowFocus: "always",
    
  });
};

export const getInterestsByAdPlatformQueryOptions = (
  adPlatformId,
  queryClient
) =>
  queryOptions({
    queryKey: ["adPlatform.interests", adPlatformId],
    queryFn: () => api.getInterestsByAdPlatform(adPlatformId),
    refetchOnWindowFocus: "always",
    
    initialData: () =>
      queryClient
        .getQueryData(["interests"])
        ?.filter((interest) => interest.adPlatform?.id === adPlatformId),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(["interests"])?.dataUpdatedAt,
  });

export const getInterestQueryOptions = (id, queryClient) => {
  let queryKey;

  return queryOptions({
    queryKey: ["interest", id],
    queryFn: () => api.getInterest(id),
    refetchOnWindowFocus: "always",
    
    initialData: () => {
      // if "interests" query exist, we use it to init data
      if (queryClient.getQueryState(["interests"])) {
        queryKey = ["interests"];
        return queryClient
          .getQueryData(["interests"])
          ?.find((interest) => interest.id === id);
      }

      // otherwise, we try to find the interest in one of the "adPlatform.interests" queries
      let found;

      queryClient
        .getQueriesData({ queryKey: ["adPlatform.interests"] })
        .forEach((query) => {
          const data = query[1];
          data.forEach((interest) => {
            if (interest.id === id) {
              found = interest;
              queryKey = query[0];
            }
          });
        });

      return found;
    },
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(queryKey)?.dataUpdatedAt,
  });
};

// useQuery

export const useSearchInterests = (
  adPlatformId,
  adAccountId,
  languageId,
  searchTerm,
  enabled = false
) => {
  return useQuery({
    ...searchInterestsQueryOptions(adPlatformId, adAccountId, languageId, searchTerm),
    enabled, // won’t auto-run until refetch() is called
  });
};

export const useGetInterests = () => useQuery(getInterestsQueryOptions());

export const useGetInterest = (id) => {
  const queryClient = useQueryClient();
  return useQuery(getInterestQueryOptions(id, queryClient));
};

export const useGetInterestsByAdPlatform = (adPlatformId) => {
  const queryClient = useQueryClient();
  return useQuery(
    getInterestsByAdPlatformQueryOptions(adPlatformId, queryClient)
  );
};

// useSuspenseQuery

export const useSuspenseGetInterests = () => {
  return useSuspenseQuery(getInterestsQueryOptions());
};

export const useSuspenseGetInterestsByAdPlatform = (adPlatformId) => {
  const queryClient = useQueryClient();
  return useSuspenseQuery(
    getInterestsByAdPlatformQueryOptions(adPlatformId, queryClient)
  );
};

export const useSuspenseGetInterest = (id) => {
  const queryClient = useQueryClient();
  return useSuspenseQuery(getInterestQueryOptions(id, queryClient));
};
